import React, { useState } from "react";

/**
 * Chrome / Safari block `target="_blank"` on `data:` URLs for security reasons,
 * and popup blockers often kill `window.open(blob:…)` calls too. This helper
 * keeps images in-app via a lightbox preview modal — the most reliable way to
 * show a stored attachment. For non-image files it falls back to a download flow.
 */

const IMG_RE = /\.(png|jpe?g|gif|webp|svg|heic|heif|bmp)$/i;

function looksLikeImage(url, name) {
  if (!url) return false;
  if (typeof url === "string" && url.startsWith("data:image")) return true;
  if (name && IMG_RE.test(name)) return true;
  if (typeof url === "string" && IMG_RE.test(url.split("?")[0])) return true;
  return false;
}

export function openFileInNewTab(url, downloadName) {
  if (!url) return;
  if (typeof url === "string" && url.startsWith("data:")) {
    try {
      const [meta, b64] = url.split(",");
      const mime = /data:([^;]+)/.exec(meta)?.[1] || "application/octet-stream";
      const bin = atob(b64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      const blob = new Blob([bytes], { type: mime });
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = downloadName || `file.${mime.split("/")[1] || "bin"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
    } catch {
      const a = document.createElement("a");
      a.href = url;
      a.download = downloadName || "file";
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
    return;
  }
  window.open(url, "_blank", "noopener");
}

/**
 * Attachment link — for images opens an in-app lightbox modal. For other file
 * types uses a download-anchor. Never relies on window.open which browsers block.
 */
export function AttachmentLink({ url, name, children, className = "btn-ghost text-xs", ...rest }) {
  const [open, setOpen] = useState(false);
  if (!url) return null;
  const isImage = looksLikeImage(url, name);

  const handleClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (isImage) {
      setOpen(true);
    } else {
      openFileInNewTab(url, name);
    }
  };

  return (
    <>
      <button type="button" onClick={handleClick} className={className} {...rest}>
        {children}
      </button>
      {open && (
        <div className="fixed inset-0 z-[80] bg-black/80 flex items-center justify-center p-4" onClick={() => setOpen(false)} data-testid="attachment-lightbox">
          <div className="relative max-w-5xl w-full" onClick={e => e.stopPropagation()}>
            <img src={url} alt={name || "attachment"} className="w-full max-h-[85vh] object-contain rounded" />
            <div className="mt-3 flex items-center justify-center gap-2">
              <button className="btn-ghost text-xs bg-white" onClick={() => openFileInNewTab(url, name)} data-testid="attachment-download">Download</button>
              <button className="btn-primary text-xs" onClick={() => setOpen(false)} data-testid="attachment-close">Close</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
